import type {
  CompartmentDTO,
  FloorPlanIconsDTO,
  InternalDTO,
  PlaceDTO,
  SignDTO,
} from '@/types/dto/agent';
import {
  type OrientationConfig,
  PlaceType,
  type TypeIdTemplateLiteral,
} from '@/types/seatSelection';

export const SEAT_ORIENTATION_MAP: Record<string, OrientationConfig> = {
  'ORIENTATION.UP': {
    degrees: 0,
    elementOffset: { x: 0, y: 0 },
    priceStripOffset: { x: -20, y: 10 },
  },
  'ORIENTATION.RIGHT': {
    degrees: 90,
    elementOffset: { x: 0, y: 0 },
    priceStripOffset: { x: -20, y: 10 },
  },
  'ORIENTATION.DOWN': {
    degrees: 180,
    elementOffset: { x: 0, y: 0 },
    priceStripOffset: { x: -20, y: 10 },
  },
  'ORIENTATION.LEFT': {
    degrees: 270,
    elementOffset: { x: 0, y: 0 },
    priceStripOffset: { x: -20, y: 10 },
  },
};

export const BERTH_ORIENTATION_MAP: Record<string, OrientationConfig> = {
  'ORIENTATION.UP': {
    degrees: 0,
    elementOffset: { x: 0, y: 30 },
    priceStripOffset: { x: 10, y: 40 },
  },
  'ORIENTATION.RIGHT': {
    degrees: 90,
    elementOffset: { x: -30, y: 0 },
    priceStripOffset: { x: 10, y: 40 },
  },
  'ORIENTATION.DOWN': {
    degrees: 180,
    elementOffset: { x: 0, y: -33 },
    priceStripOffset: { x: 10, y: 40 },
  },
  'ORIENTATION.LEFT': {
    degrees: 270,
    elementOffset: { x: 30, y: 0 },
    priceStripOffset: { x: 10, y: 40 },
  },
};

export const RECLINER_ORIENTATION_MAP: Record<string, OrientationConfig> = {
  'ORIENTATION.UP': {
    degrees: 0,
    elementOffset: { x: 0, y: 10 },
    priceStripOffset: { x: -20, y: 30 },
  },
  'ORIENTATION.RIGHT': {
    degrees: 90,
    elementOffset: { x: -5, y: 1 },
    priceStripOffset: { x: -20, y: 15 },
  },
  'ORIENTATION.DOWN': {
    degrees: 180,
    elementOffset: { x: 0, y: -5 },
    priceStripOffset: { x: -20, y: 35 },
  },
  'ORIENTATION.LEFT': {
    degrees: 270,
    elementOffset: { x: 5, y: 1 },
    priceStripOffset: { x: -20, y: 15 },
  },
};

// PLACE_TYPE and INTERNAL_TYPE elements have been migrated to OSDM standard (ST-90)
export const databaseToUserInterfaceOrientationMap: Record<string, string> = {
  'ORIENTATION.UP': 'ORIENTATION.LEFT',
  'ORIENTATION.RIGHT': 'ORIENTATION.UP',
  'ORIENTATION.DOWN': 'ORIENTATION.RIGHT',
  'ORIENTATION.LEFT': 'ORIENTATION.DOWN',
};

export const getOrientationConfig = (
  typeId: TypeIdTemplateLiteral,
  orientationId: string | undefined = 'ORIENTATION.UP'
): OrientationConfig => {
  // SIGN_TYPE elements don't have orientation defined, thus we just want to display them UP
  if (typeId.includes('SIGN_TYPE')) {
    return SEAT_ORIENTATION_MAP[orientationId];
  }

  const convertedOrientationId =
    databaseToUserInterfaceOrientationMap[orientationId];

  if (typeId === PlaceType.BERTH) {
    return BERTH_ORIENTATION_MAP[convertedOrientationId];
  } else if (typeId === PlaceType.RECLINER) {
    return RECLINER_ORIENTATION_MAP[convertedOrientationId];
  } else if (typeId === PlaceType.SEAT) {
    return SEAT_ORIENTATION_MAP[convertedOrientationId];
  }

  return SEAT_ORIENTATION_MAP[convertedOrientationId];
};

export const transformCoordinates = (
  x: number,
  y: number,
  canvasHeight: number
): { x: number; y: number } => ({
  x: x,
  y: canvasHeight - y,
});

export const getPassengerInitials = (
  passengerInfo:
    | {
        firstName: string;
        lastName: string;
        reservationId?: string;
        index: number;
      }
    | undefined
): string => {
  if (!passengerInfo) {
    return '';
  }

  const { firstName, lastName, index } = passengerInfo;

  if (!firstName && !lastName) {
    return `P${index + 1}`;
  }

  const firstInitial = firstName ? firstName.charAt(0).toUpperCase() : '';
  const lastInitial = lastName ? lastName.charAt(0).toUpperCase() : '';

  return `${firstInitial}${lastInitial}`;
};

export const groupNestedElements = (
  elements: Array<PlaceDTO>
): Array<Array<PlaceDTO>> => {
  const nestedElements = elements.filter((el) => !!el.nestedInElementId);
  const parentIds = new Set(nestedElements.map((e) => e.nestedInElementId));

  return Array.from(parentIds).reduce<Array<Array<PlaceDTO>>>(
    (acc, parentId) => {
      const parent = elements.find((e) => e.id === parentId);
      if (!parent) return acc;

      const nested = nestedElements
        .filter((e) => e.nestedInElementId === parentId)
        .sort((a, b) => {
          const orderA = a.nestingOrderNumber
            ? parseInt(a.nestingOrderNumber, 10)
            : 0;
          const orderB = b.nestingOrderNumber
            ? parseInt(b.nestingOrderNumber, 10)
            : 0;
          return orderA - orderB;
        });

      return [...acc, [parent, ...nested]];
    },
    []
  );
};

export const getElementTypeId = (
  element: PlaceDTO | SignDTO | InternalDTO,
  floorPlanIcons: FloorPlanIconsDTO | undefined
): TypeIdTemplateLiteral | undefined => {
  const { elementId } = element;

  if (floorPlanIcons?.floorPlanIcons) {
    const matchingIcons = floorPlanIcons.floorPlanIcons.filter(
      (icon) => icon.elementId === elementId
    );

    if (matchingIcons.length > 0) {
      const firstMatchingIcon = matchingIcons[0];

      if (firstMatchingIcon) {
        return firstMatchingIcon.typeId as TypeIdTemplateLiteral;
      }
    }
  }

  return undefined;
};

export const PLACE_ICONS = {
  BLOCKED: `
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path 
        fill-rule="evenodd" 
        clip-rule="evenodd" 
        d="M5.2853 14.7139C7.8888 17.3174 12.1099 17.3174 14.7134 14.7139C17.3169 12.1104 17.3169 7.88929 14.7134 5.28579C12.1099 2.6823 7.8888 2.6823 5.2853 5.28579C2.68181 7.88929 2.68181 12.1104 5.2853 14.7139ZM4.10679 15.8924C7.36116 19.1468 12.6375 19.1468 15.8919 15.8924C19.1463 12.638 19.1463 7.36165 15.8919 4.10728C12.6375 0.852912 7.36116 0.852912 4.10679 4.10728C0.852423 7.36165 0.852422 12.638 4.10679 15.8924Z" 
        fill="#5A5F8C"
      />
      <path 
        fill-rule="evenodd" 
        clip-rule="evenodd" 
        d="M16.0284 4.4346C16.3405 4.77278 16.3194 5.3 15.9813 5.61217L5.14792 15.6122C4.80974 15.9243 4.28253 15.9032 3.97036 15.5651C3.65819 15.2269 3.67927 14.6997 4.01746 14.3875L14.8508 4.38749C15.189 4.07532 15.7162 4.09641 16.0284 4.4346Z" 
        fill="#5A5F8C"
      />
    </svg>
  `,
  BOOKED: `
    <svg
      viewBox="0 0 12 13"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      width="12"
      height="13"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M9.35355 3.4465C9.54882 3.64176 9.54882 3.95834 9.35355 4.1536L3.35355 10.1536C3.15829 10.3489 2.84171 10.3489 2.64645 10.1536C2.45118 9.95834 2.45118 9.64176 2.64645 9.4465L8.64645 3.4465C8.84171 3.25123 9.15829 3.25123 9.35355 3.4465Z"
        fill="#5A5F8C"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M2.64645 3.4465C2.84171 3.25123 3.15829 3.25123 3.35355 3.4465L9.35355 9.4465C9.54882 9.64176 9.54882 9.95834 9.35355 10.1536C9.15829 10.3489 8.84171 10.3489 8.64645 10.1536L2.64645 4.1536C2.45118 3.95834 2.45118 3.64176 2.64645 3.4465Z"
        fill="#5A5F8C"
      />
    </svg>
  `,
  WHEELCHAIR: `
    <svg viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M7.27072 6.28215C7.71324 6.1557 8.17449 6.41193 8.30094 6.85446L8.96157 9.16644L11.6663 9.16644C12.1266 9.16644 12.4997 9.53954 12.4997 9.99978C12.4997 10.46 12.1266 10.8331 11.6663 10.8331L8.33301 10.8331C7.96095 10.8331 7.63397 10.5865 7.53174 10.2287L6.69841 7.31237C6.57196 6.86985 6.82819 6.4086 7.27072 6.28215ZM5.94078 9.82167C6.26944 10.1439 6.27468 10.6715 5.95249 11.0001C5.36224 11.6022 4.99967 12.4246 4.99967 13.3334C4.99967 15.1744 6.49206 16.6667 8.33301 16.6667C9.42302 16.6667 10.3908 16.1444 11.0003 15.3329C11.2767 14.9649 11.7991 14.8907 12.1671 15.1671C12.5351 15.4435 12.6094 15.9659 12.333 16.3339C11.4222 17.5465 9.96936 18.3334 8.33301 18.3334C5.57158 18.3334 3.33301 16.0948 3.33301 13.3334C3.33301 11.971 3.87904 10.7344 4.76233 9.83338C5.08451 9.50472 5.61213 9.49948 5.94078 9.82167Z"
        fill="currentColor"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M9.99967 12.5001C9.99967 12.0398 10.3728 11.6667 10.833 11.6667H14.767L16.6236 17.2366C16.7691 17.6732 16.5332 18.1451 16.0965 18.2907C15.6599 18.4362 15.188 18.2002 15.0424 17.7636L13.5657 13.3334H10.833C10.3728 13.3334 9.99967 12.9603 9.99967 12.5001Z"
        fill="currentColor"
      />
      <path
        d="M9.16634 3.33341C9.16634 4.25389 8.42015 5.00008 7.49967 5.00008C6.5792 5.00008 5.83301 4.25389 5.83301 3.33341C5.83301 2.41294 6.5792 1.66675 7.49967 1.66675C8.42015 1.66675 9.16634 2.41294 9.16634 3.33341Z"
        fill="currentColor"
      />
    </svg>
  `,
} as const;

export const getElementState = <
  T extends string | number | object | boolean | undefined,
>(
  {
    id,
    reservationId,
    isAvailable,
    isBlocked,
    properties,
    selectedReservationId,
    hoveredSeatId,
  }: (PlaceDTO | CompartmentDTO) & {
    selectedReservationId: string | undefined;
    hoveredSeatId: string | undefined;
  },
  {
    elementPartOfBookingAndSelected,
    elementPartOfBookingAndNotSelected,
    elementPartOfBookingAndNotSelectedAndHovered,
    elementAvailableWithWheelchair,
    elementAvailableWithWheelchairAndHovered,
    elementAvailable,
    elementAvailableAndHovered,
    elementBlocked,
    elementBooked,
  }: Record<
    | 'elementPartOfBookingAndSelected'
    | 'elementPartOfBookingAndNotSelected'
    | 'elementPartOfBookingAndNotSelectedAndHovered'
    | 'elementAvailableWithWheelchair'
    | 'elementAvailableWithWheelchairAndHovered'
    | 'elementAvailable'
    | 'elementAvailableAndHovered'
    | 'elementBlocked'
    | 'elementBooked',
    T
  >
): T => {
  if (reservationId) {
    if (reservationId === selectedReservationId) {
      return elementPartOfBookingAndSelected;
    } else {
      if (id === hoveredSeatId) {
        return elementPartOfBookingAndNotSelectedAndHovered;
      } else {
        return elementPartOfBookingAndNotSelected;
      }
    }
  }

  if (isBlocked) {
    return elementBlocked;
  }

  if (isAvailable) {
    if (properties?.some((property) => property.id === 'WHEELCHAIR_AREA')) {
      if (id === hoveredSeatId) {
        return elementAvailableWithWheelchairAndHovered;
      } else {
        return elementAvailableWithWheelchair;
      }
    } else {
      if (id === hoveredSeatId) {
        return elementAvailableAndHovered;
      } else {
        return elementAvailable;
      }
    }
  }

  return elementBooked;
};
